import { Field } from '@/components/Fields';
import { getUserInfo } from '@/features/auth';
import { useAppDispatch, useErrorIndicator } from '@/hooks';
import {
  Button,
  Center,
  Link as ChakraLink,
  Flex,
  FormControl,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useState } from 'react';
import Session from 'supertokens-web-js/recipe/session';
import Totp from 'supertokens-web-js/recipe/totp';
import * as Yup from 'yup';

interface VerifyFormValues {
  code: string;
}

interface TwoFactorVerifyViewProps {
  onSuccess: () => void;
  onUseRecoveryCode: () => void;
}

export const TwoFactorVerifyView: React.FC<TwoFactorVerifyViewProps> = ({
  onSuccess,
  onUseRecoveryCode,
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { showError } = useErrorIndicator();

  const handleSubmit = async (
    values: VerifyFormValues,
    { setSubmitting }: FormikHelpers<VerifyFormValues>,
  ) => {
    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await Totp.verifyCode({
        totp: values.code,
      });

      if (response.status === 'OK') {
        await dispatch(getUserInfo());
        onSuccess();
        router.push('/results');
      } else if (response.status === 'INVALID_TOTP_ERROR') {
        const attemptsLeft =
          response.maxNumberOfFailedAttempts - response.currentNumberOfFailedAttempts;
        if (attemptsLeft > 0) {
          setErrorMessage(`Invalid code. ${attemptsLeft} attempts remaining.`);
        } else {
          setErrorMessage('Invalid code. Too many failed attempts.');
        }
      } else if (response.status === 'LIMIT_REACHED_ERROR') {
        const minutes = Math.ceil((response.retryAfterMs || 900000) / 60000);
        setErrorMessage(
          `Too many failed attempts. Please wait ${minutes} minutes before trying again.`,
        );
      } else {
        setErrorMessage('Verification failed. Please try again.');
      }
    } catch (error) {
      showError('Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await Session.signOut();
      router.push('/');
    } catch (error) {
      showError('Failed to sign out. Please try again.');
      setIsLoggingOut(false);
    }
  };

  const VerifySchema = Yup.object().shape({
    code: Yup.string()
      .matches(/^\d{6}$/, 'Code must be exactly 6 digits')
      .required('Required'),
  });

  return (
    <Center>
      <VStack gap={12}>
        <VStack gap={2}>
          <Heading textAlign="center">Two-Factor Authentication</Heading>
          <Text color="gray.400" textAlign="center">
            Enter the 6-digit code from your authenticator app
          </Text>
        </VStack>

        <Formik
          initialValues={{ code: '' }}
          onSubmit={handleSubmit}
          validationSchema={VerifySchema}
        >
          {({ isSubmitting, isValid, dirty, errors, touched, setFieldValue }) => (
            <Form>
              <FormControl alignItems="center" display="flex" flexDir="column">
                <Field
                  type="text"
                  onDark
                  placeholder="000000"
                  name="code"
                  errors={errors.code}
                  touched={touched.code}
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  h="56px"
                  pl={3}
                  textAlign="center"
                  fontSize="2xl"
                  letterSpacing="0.5em"
                  fontFamily="mono"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setFieldValue('code', value);
                  }}
                  full
                />

                {errorMessage && (
                  <Text mt={4} mb={2} color="red.400" textAlign="center">
                    {errorMessage}
                  </Text>
                )}

                <Button
                  fontWeight="semibold"
                  variant="primary"
                  size="xl"
                  w="full"
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  mt={6}
                  type="submit"
                  isDisabled={!isValid || !dirty || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Verify
                </Button>
              </FormControl>
            </Form>
          )}
        </Formik>

        <Flex gap={1} flexDir="column" alignItems="center">
          <Text color="gray.500" fontSize="sm">
            Lost access to your authenticator?
          </Text>
          <ChakraLink color="teal.500" cursor="pointer" onClick={onUseRecoveryCode} fontSize="sm">
            Use a recovery code
          </ChakraLink>
        </Flex>

        <Flex gap={1} flexDir="column" alignItems="center" mt={4}>
          <ChakraLink
            color="gray.500"
            cursor={isLoggingOut ? 'not-allowed' : 'pointer'}
            onClick={isLoggingOut ? undefined : handleSignOut}
            fontSize="sm"
            opacity={isLoggingOut ? 0.5 : 1}
            pointerEvents={isLoggingOut ? 'none' : 'auto'}
            _hover={{ color: 'gray.400' }}
          >
            {isLoggingOut ? 'Signing out...' : 'Sign in with a different account'}
          </ChakraLink>
        </Flex>
      </VStack>
    </Center>
  );
};
